import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { 
  Target, Zap, Users, Building2, Award, ArrowRight, 
  CheckCircle, Briefcase, Globe, TrendingUp, Shield,
  Layers, MessageSquare, FileText, Star, ChevronRight,
  Newspaper, Share2, Package
} from 'lucide-react';

const About = () => {
  const services = [
    { icon: Newspaper, title: "Digital PR & Publications", desc: "Get featured in 50+ premium publications including Forbes, YourStory, Inc42, and more" },
    { icon: Share2, title: "Social Media Promotions", desc: "Leverage 100+ influencers and meme pages across Instagram, LinkedIn, YouTube & Twitter" },
    { icon: Package, title: "Multi-Channel Bundles", desc: "Save up to 35% with curated packages combining Digital PR and Social Media" },
    { icon: Award, title: "Award & Recognition Amplification", desc: "Maximize the impact of your achievements across all media channels" }
  ];

  const differentiators = [
    { title: "Multi-Channel Approach", desc: "One platform for all your media needs - Digital PR, Social Media, and Bundles. No more juggling multiple vendors." },
    { title: "Execution-Focused", desc: "We deliver results, not pitches. Every service comes with clear deliverables and guaranteed placements." },
    { title: "Built for Scale", desc: "From single placements to enterprise campaigns, our infrastructure handles any volume without compromising quality." }
  ];

  const audiences = [
    { icon: Users, title: "Founders & Entrepreneurs", desc: "Build credibility across digital and social media" },
    { icon: TrendingUp, title: "Startups & Growth Companies", desc: "Multi-channel visibility for every growth milestone" },
    { icon: Building2, title: "Corporates & Institutions", desc: "Comprehensive media execution at enterprise scale" },
    { icon: Briefcase, title: "Marketing & PR Agencies", desc: "White-label execution partner across all channels" }
  ];

  const stats = [
    { value: "50+", label: "Digital Publications" },
    { value: "100+", label: "Social Influencers" },
    { value: "500+", label: "Articles Published" },
    { value: "35%", label: "Bundle Savings" }
  ];

  const philosophyPoints = [
    "Visibility should span multiple channels, not be siloed",
    "Media execution should be simple, not fragmented",
    "Results should be guaranteed, not promised"
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="About Us"
        description="BusinessMint PR is India's leading multi-channel media execution platform. Get featured across Digital PR, Social Media, and curated Bundles - all from one place."
        keywords="about BusinessMint PR, PR agency India, media execution, Digital PR, Social Media Marketing, PR Bundles, founder PR, business visibility"
        canonical="/about"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "BusinessMint PR",
          "description": "Multi-channel media execution platform for Digital PR, Social Media, and Bundles",
          "url": "https://businessmint.in",
          "logo": "https://businessmint.in/logo.png",
          "foundingDate": "2024",
          "areaServed": "India",
          "serviceType": ["Digital PR", "Social Media Marketing", "PR Bundles", "Media Coverage"]
        }}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-[#1a1a1a] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-[#D4AF37] rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#D4AF37] rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-[#D4AF37]/20 text-[#D4AF37] rounded-full font-manrope text-sm mb-6">
              About BusinessMint PR
            </span>
            <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Your Brand. <br />
              <span className="text-[#D4AF37]">Every Platform.</span>
            </h1>
            <p className="font-manrope text-lg md:text-xl text-gray-300 leading-relaxed mb-8">
              BusinessMint PR is India's first multi-channel media execution platform. 
              We bring Digital PR, Social Media, and Bundles under one roof—
              so you can amplify your brand across every channel that matters.
            </p>
            <p className="font-playfair text-2xl text-white/90 italic">
              One platform. Three service types. Unlimited visibility.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#D4AF37] py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index}>
                <p className="font-playfair text-3xl md:text-4xl font-bold text-[#0d0d0d]">{stat.value}</p>
                <p className="font-manrope text-sm text-[#0d0d0d]/80">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-[#fee2e2] text-[#D4AF37] rounded-full font-manrope text-sm mb-4">
              What We Do
            </span>
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">
              Multi-Channel Media Execution
            </h2>
            <p className="font-manrope text-lg text-[#737373] max-w-2xl mx-auto">
              From online publications to social influencers—
              we execute your media strategy across every channel.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div 
                key={index}
                className="group p-8 border border-[#e5e5e5] hover:border-[#D4AF37] transition-all duration-300 hover:shadow-lg"
              >
                <div className="w-14 h-14 bg-[#fee2e2] rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#D4AF37] transition-colors">
                  <service.icon className="w-7 h-7 text-[#D4AF37] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-playfair text-xl font-bold text-[#1a1a1a] mb-3">
                  {service.title}
                </h3>
                <p className="font-manrope text-[#737373]">
                  {service.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Value Props */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="p-6">
              <p className="font-playfair text-2xl font-bold text-[#D4AF37] mb-2">No Retainers</p>
              <p className="font-manrope text-[#737373]">Pay per placement, not monthly fees</p>
            </div>
            <div className="p-6">
              <p className="font-playfair text-2xl font-bold text-[#D4AF37] mb-2">100% Guaranteed</p>
              <p className="font-manrope text-[#737373]">Every placement comes with proof of delivery</p>
            </div>
            <div className="p-6">
              <p className="font-playfair text-2xl font-bold text-[#D4AF37] mb-2">48-Hour Express</p>
              <p className="font-manrope text-[#737373]">Fast-track options for urgent campaigns</p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Types Section */}
      <section className="py-20 md:py-28 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-[#fee2e2] text-[#D4AF37] rounded-full font-manrope text-sm mb-4">
              Four Service Types
            </span>
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">
              Complete Media Coverage
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Digital PR */}
            <div className="bg-white p-8 border border-[#e5e5e5] hover:border-[#D4AF37] transition-all">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-[#D4AF37] rounded-lg flex items-center justify-center">
                  <Newspaper className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-playfair text-2xl font-bold text-[#1a1a1a]">Digital PR</h3>
              </div>
              <p className="font-manrope text-[#737373] mb-4">
                Get featured in premium online publications with high domain authority. 
                Perfect for SEO, credibility building, and thought leadership.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 font-manrope text-sm text-[#4a4a4a]">
                  <CheckCircle className="w-4 h-4 text-[#D4AF37]" />
                  50+ verified publications
                </li>
                <li className="flex items-center gap-2 font-manrope text-sm text-[#4a4a4a]">
                  <CheckCircle className="w-4 h-4 text-[#D4AF37]" />
                  DA 40+ publications available
                </li>
                <li className="flex items-center gap-2 font-manrope text-sm text-[#4a4a4a]">
                  <CheckCircle className="w-4 h-4 text-[#D4AF37]" />
                  Permanent backlinks included
                </li>
              </ul>
              <Link to="/services/digital-pr" className="inline-flex items-center gap-2 text-[#D4AF37] font-manrope text-sm hover:underline">
                Learn More <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Social Media */}
            <div className="bg-white p-8 border border-[#e5e5e5] hover:border-[#D4AF37] transition-all">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-[#D4AF37] rounded-lg flex items-center justify-center">
                  <Share2 className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-playfair text-2xl font-bold text-[#1a1a1a]">Social Media</h3>
              </div>
              <p className="font-manrope text-[#737373] mb-4">
                Tap into viral potential with meme pages, influencers, and publication 
                social handles across all major platforms.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 font-manrope text-sm text-[#4a4a4a]">
                  <CheckCircle className="w-4 h-4 text-[#D4AF37]" />
                  100+ influencers & pages
                </li>
                <li className="flex items-center gap-2 font-manrope text-sm text-[#4a4a4a]">
                  <CheckCircle className="w-4 h-4 text-[#D4AF37]" />
                  Instagram, LinkedIn, YouTube, Twitter
                </li>
                <li className="flex items-center gap-2 font-manrope text-sm text-[#4a4a4a]">
                  <CheckCircle className="w-4 h-4 text-[#D4AF37]" />
                  10M+ combined reach
                </li>
              </ul>
              <Link to="/services/social-media" className="inline-flex items-center gap-2 text-[#D4AF37] font-manrope text-sm hover:underline">
                Learn More <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Print Media - TEMPORARILY HIDDEN
            <div className="bg-white p-8 border border-[#e5e5e5] hover:border-[#D4AF37] transition-all">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-[#D4AF37] rounded-lg flex items-center justify-center">
                  <Printer className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-playfair text-2xl font-bold text-[#1a1a1a]">Print Media</h3>
              </div>
              <p className="font-manrope text-[#737373] mb-4">
                Establish authority with placements in leading newspapers and magazines. 
                The credibility of print, executed seamlessly.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 font-manrope text-sm text-[#4a4a4a]">
                  <CheckCircle className="w-4 h-4 text-[#D4AF37]" />
                  50M+ daily readers
                </li>
                <li className="flex items-center gap-2 font-manrope text-sm text-[#4a4a4a]">
                  <CheckCircle className="w-4 h-4 text-[#D4AF37]" />
                  National & regional coverage
                </li>
                <li className="flex items-center gap-2 font-manrope text-sm text-[#4a4a4a]">
                  <CheckCircle className="w-4 h-4 text-[#D4AF37]" />
                  Newspapers & magazines
                </li>
              </ul>
              <Link to="/services/print-media" className="inline-flex items-center gap-2 text-[#D4AF37] font-manrope text-sm hover:underline">
                Learn More <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            END TEMPORARILY HIDDEN */}

            {/* Bundles */}
            <div className="bg-white p-8 border border-[#e5e5e5] hover:border-[#D4AF37] transition-all">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-[#D4AF37] rounded-lg flex items-center justify-center">
                  <Package className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-playfair text-2xl font-bold text-[#1a1a1a]">Bundles</h3>
              </div>
              <p className="font-manrope text-[#737373] mb-4">
                Maximize impact with curated multi-channel packages. Combine Digital PR 
                and Social Media for comprehensive coverage at discounted rates.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 font-manrope text-sm text-[#4a4a4a]">
                  <CheckCircle className="w-4 h-4 text-[#D4AF37]" />
                  Save up to 35%
                </li>
                <li className="flex items-center gap-2 font-manrope text-sm text-[#4a4a4a]">
                  <CheckCircle className="w-4 h-4 text-[#D4AF37]" />
                  Cross-channel packages
                </li>
                <li className="flex items-center gap-2 font-manrope text-sm text-[#4a4a4a]">
                  <CheckCircle className="w-4 h-4 text-[#D4AF37]" />
                  Startup to Enterprise options
                </li>
              </ul>
              <Link to="/services/bundles" className="inline-flex items-center gap-2 text-[#D4AF37] font-manrope text-sm hover:underline">
                Learn More <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How We're Different Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-[#fee2e2] text-[#D4AF37] rounded-full font-manrope text-sm mb-4">
              How We're Different
            </span>
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">
              Not a PR Agency. A Media Platform.
            </h2>
            <p className="font-manrope text-lg text-[#737373] max-w-2xl mx-auto">
              We're not a traditional agency that juggles relationships. We're a platform 
              that delivers guaranteed placements across multiple channels.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {differentiators.map((item, index) => (
              <div 
                key={index}
                className="relative p-8 bg-[#fafafa] border-l-4 border-[#D4AF37]"
              >
                <div className="absolute -top-4 -left-4 w-10 h-10 bg-[#D4AF37] text-[#0d0d0d] flex items-center justify-center font-playfair text-xl font-bold">
                  {index + 1}
                </div>
                <h3 className="font-playfair text-xl font-bold text-[#1a1a1a] mb-3 mt-2">
                  {item.title}
                </h3>
                <p className="font-manrope text-[#737373]">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-16 p-8 bg-[#1a1a1a] text-white text-center">
            <p className="font-manrope text-lg mb-4">
              Stop managing multiple vendors for different channels. With BusinessMint PR, 
              get Digital PR, Social Media, and Bundles—all from one dashboard.
            </p>
            <p className="font-playfair text-xl italic text-[#D4AF37]">
              One platform. Complete visibility.
            </p>
          </div>
        </div>
      </section>

      {/* Who We Work With Section */}
      <section className="py-20 md:py-28 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-[#fee2e2] text-[#D4AF37] rounded-full font-manrope text-sm mb-4">
              Who We Work With
            </span>
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">
              From Single Placements to Full Campaigns
            </h2>
            <p className="font-manrope text-lg text-[#737373] max-w-2xl mx-auto">
              Whether you need one article or a multi-channel campaign, 
              BusinessMint PR scales to your requirement.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {audiences.map((audience, index) => (
              <div 
                key={index}
                className="group p-8 bg-white border border-[#e5e5e5] hover:border-[#D4AF37] transition-all duration-300 text-center"
              >
                <div className="w-16 h-16 mx-auto bg-[#fee2e2] rounded-full flex items-center justify-center mb-6 group-hover:bg-[#D4AF37] transition-colors">
                  <audience.icon className="w-8 h-8 text-[#D4AF37] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-playfair text-lg font-bold text-[#1a1a1a] mb-2">
                  {audience.title}
                </h3>
                <p className="font-manrope text-sm text-[#737373]">
                  {audience.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 md:py-28 bg-[#1a1a1a] text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <span className="inline-block px-4 py-2 bg-[#D4AF37]/20 text-[#D4AF37] rounded-full font-manrope text-sm mb-6">
            Our Philosophy
          </span>
          <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-12">
            What We Believe
          </h2>
          
          <div className="space-y-8">
            {philosophyPoints.map((point, index) => (
              <div 
                key={index}
                className="flex items-center justify-center gap-4"
              >
                <div className="w-2 h-2 bg-[#D4AF37] rounded-full"></div>
                <p className="font-playfair text-xl md:text-2xl italic text-gray-200">
                  {point}
                </p>
              </div>
            ))}
          </div>
          
          <p className="font-manrope text-lg text-gray-400 mt-12">
            BusinessMint PR exists to make multi-channel media <span className="text-white">simple, professional, and effective</span>.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-6">
            Ready to Amplify Your Brand?
          </h2>
          <p className="font-manrope text-lg text-[#737373] mb-8">
            Whether you need Digital PR, Social Media buzz, 
            or a comprehensive multi-channel campaign—
          </p>
          <p className="font-playfair text-2xl text-[#D4AF37] italic mb-10">
            We've got you covered.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/marketplace"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#D4AF37] text-[#0d0d0d] font-manrope text-sm uppercase tracking-widest hover:bg-[#B8960F] transition-colors"
            >
              Explore Services
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-[#1a1a1a] text-[#1a1a1a] font-manrope text-sm uppercase tracking-widest hover:bg-[#1a1a1a] hover:text-white transition-all"
            >
              Get Started
            </Link>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default About;
